import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Badge,
  DrawerBody,
  DrawerFooter,
  SkeletonCard,
  useToast,
  ConfirmDialog,
} from "@payflow/ui";
import { useAuth } from "../../contexts/AuthContext";
import {
  listRoles,
  deleteRole,
  type RoleResponse,
  type CreateRoleData,
  type UpdateRoleData,
} from "../../services/roles.service";

interface Permission {
  id: string;
  nome: string;
  grupo: string;
}

// Lista compartilhada de permissoes (usada na Page e na Detail).
export function useAvailablePermissions(): Permission[] {
  const { t } = useTranslation();
  return useMemo(() => [
    { id: "usuarios:listar", nome: t("permissions.usuarios:listar"), grupo: t("permissions.usuarios") },
    { id: "usuarios:visualizar", nome: t("permissions.usuarios:visualizar"), grupo: t("permissions.usuarios") },
    { id: "usuarios:criar", nome: t("permissions.usuarios:criar"), grupo: t("permissions.usuarios") },
    { id: "usuarios:editar", nome: t("permissions.usuarios:editar"), grupo: t("permissions.usuarios") },
    { id: "usuarios:excluir", nome: t("permissions.usuarios:excluir"), grupo: t("permissions.usuarios") },
    { id: "perfis:listar", nome: t("permissions.perfis:listar"), grupo: t("permissions.perfis") },
    { id: "perfis:visualizar", nome: t("permissions.perfis:visualizar"), grupo: t("permissions.perfis") },
    { id: "perfis:criar", nome: t("permissions.perfis:criar"), grupo: t("permissions.perfis") },
    { id: "perfis:editar", nome: t("permissions.perfis:editar"), grupo: t("permissions.perfis") },
    { id: "perfis:excluir", nome: t("permissions.perfis:excluir"), grupo: t("permissions.perfis") },
    { id: "clientes:listar", nome: t("permissions.clientes:listar"), grupo: t("permissions.clientes") },
    { id: "clientes:visualizar", nome: t("permissions.clientes:visualizar"), grupo: t("permissions.clientes") },
    { id: "clientes:criar", nome: t("permissions.clientes:criar"), grupo: t("permissions.clientes") },
    { id: "clientes:editar", nome: t("permissions.clientes:editar"), grupo: t("permissions.clientes") },
    { id: "clientes:excluir", nome: t("permissions.clientes:excluir"), grupo: t("permissions.clientes") },
    { id: "emprestimos:listar", nome: t("permissions.emprestimos:listar"), grupo: t("permissions.emprestimos") },
    { id: "emprestimos:visualizar", nome: t("permissions.emprestimos:visualizar"), grupo: t("permissions.emprestimos") },
    { id: "emprestimos:criar", nome: t("permissions.emprestimos:criar"), grupo: t("permissions.emprestimos") },
    { id: "emprestimos:editar", nome: t("permissions.emprestimos:editar"), grupo: t("permissions.emprestimos") },
    { id: "emprestimos:excluir", nome: t("permissions.emprestimos:excluir"), grupo: t("permissions.emprestimos") },
    { id: "parcelas:listar", nome: t("permissions.parcelas:listar"), grupo: t("permissions.parcelas") },
    { id: "parcelas:registrar_pagamento", nome: t("permissions.parcelas:registrar_pagamento"), grupo: t("permissions.parcelas") },
  ], [t]);
}

export function RolesPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<RoleResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canCreate = hasPermission("perfis:criar");
  const canEdit = hasPermission("perfis:editar");
  const canDelete = hasPermission("perfis:excluir");

  const availablePermissions = useAvailablePermissions();

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listRoles({ page: 1, limit: 50 });
      setRoles(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("roles.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleDeleteRole = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRole(deleteTarget.id);
      setDeleteTarget(null);
      toast({ variant: "success", title: t("roles.delete_success") });
      fetchRoles();
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("roles.delete_error") });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("roles.title")}</h1>
          <p className="text-muted-foreground">
            {t("roles.subtitle")}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate("/dashboard/perfis/novo")}>
            <PlusIcon />
            {t("roles.new")}
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-error/10 p-4 text-error">{error}</div>
      )}

      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {roles.map((role) => (
            <Card key={role.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{role.nome}</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {role.descricao || t("roles.no_description")}
                    </p>
                  </div>
                  {!role.sistema && (
                    <div className="flex gap-1">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/dashboard/perfis/${role.id}`)}
                          title={t("common.edit")}
                        >
                          <EditIcon />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(role)}
                          className="text-error hover:text-error"
                          title={t("common.delete")}
                        >
                          <TrashIcon />
                        </Button>
                      )}
                    </div>
                  )}
                  {role.sistema && (
                    <Badge variant="outline" size="sm">{t("roles.system")}</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-3 flex items-center gap-1 text-sm text-muted-foreground">
                  <KeyIcon />
                  <span>{t("roles.permissions_count", { count: role.permissoes.length })}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {role.permissoes.slice(0, 4).map((permId) => {
                    const perm = availablePermissions.find((p) => p.id === permId);
                    return (
                      <Badge key={permId} variant="default" size="sm">
                        {perm?.nome || permId}
                      </Badge>
                    );
                  })}
                  {role.permissoes.length > 4 && (
                    <Badge variant="info" size="sm">
                      {t("roles.more", { count: role.permissoes.length - 4 })}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {roles.length === 0 && (
            <div className="col-span-full py-12 text-center text-muted-foreground">
              {t("roles.empty")}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("roles.delete_title")}
        description={t("roles.delete_message", { name: deleteTarget?.nome })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteRole}
      />
    </div>
  );
}

// Role form — usado em Drawer (legacy) e Page (RoleDetailPage).
export interface RoleFormProps {
  role: RoleResponse | null;
  saving: boolean;
  onSave: (data: CreateRoleData | UpdateRoleData) => void;
  availablePermissions: Permission[];
  variant?: "drawer" | "page";
}

export function RoleForm({ role, saving, onSave, availablePermissions, variant = "drawer" }: RoleFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nome: role?.nome || "",
    descricao: role?.descricao || "",
    permissoes: role?.permissoes || [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const togglePermission = (permId: string) => {
    setFormData((prev) => ({
      ...prev,
      permissoes: prev.permissoes.includes(permId)
        ? prev.permissoes.filter((p) => p !== permId)
        : [...prev.permissoes, permId],
    }));
  };

  const toggleGroup = (grupo: string) => {
    const groupPerms = availablePermissions
      .filter((p) => p.grupo === grupo)
      .map((p) => p.id);
    const allSelected = groupPerms.every((p) => formData.permissoes.includes(p));

    setFormData((prev) => ({
      ...prev,
      permissoes: allSelected
        ? prev.permissoes.filter((p) => !groupPerms.includes(p))
        : [...new Set([...prev.permissoes, ...groupPerms])],
    }));
  };

  const groupedPermissions = availablePermissions.reduce(
    (acc, perm) => {
      if (!acc[perm.grupo]) acc[perm.grupo] = [];
      acc[perm.grupo].push(perm);
      return acc;
    },
    {} as Record<string, Permission[]>
  );

  const isPage = variant === "page";
  const Body = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="min-h-0 flex-1 overflow-y-auto px-1 py-4">{children}</div>
      )
    : DrawerBody;
  const Footer = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="flex justify-end gap-2 border-t border-border pt-4">{children}</div>
      )
    : DrawerFooter;

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
      <Body>
        <div className="space-y-5">
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("roles.section_data")}
            </h3>
            <div className="space-y-4">
              <Input
                label={t("roles.label_name")}
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
              <Input
                label={t("roles.label_description")}
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("roles.label_permissions")} ({formData.permissoes.length})
            </h3>
            <div className="space-y-4 rounded-lg border border-border p-4">
              {Object.entries(groupedPermissions).map(([grupo, perms]) => {
                const allSelected = perms.every((p) => formData.permissoes.includes(p.id));
                const someSelected = perms.some((p) => formData.permissoes.includes(p.id));
                return (
                  <div key={grupo} className="rounded-lg bg-muted/30 p-3">
                    <label className="mb-2 flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                        onChange={() => toggleGroup(grupo)}
                        className="h-4 w-4 rounded border-input text-primary-600 focus:ring-primary-500"
                      />
                      <span className="font-medium text-foreground">{grupo}</span>
                      <Badge variant={allSelected ? "success" : someSelected ? "warning" : "outline"} size="sm">
                        {perms.filter((p) => formData.permissoes.includes(p.id)).length}/{perms.length}
                      </Badge>
                    </label>
                    <div className="ml-6 grid gap-2 sm:grid-cols-2">
                      {perms.map((perm) => (
                        <label key={perm.id} className="flex cursor-pointer items-start gap-2">
                          <input
                            type="checkbox"
                            checked={formData.permissoes.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            className="mt-0.5 h-4 w-4 rounded border-input text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-muted-foreground">{perm.nome}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Body>

      <Footer>
        <Button type="submit" loading={saving}>
          {role ? t("common.save") : t("common.create")}
        </Button>
      </Footer>
    </form>
  );
}

// Icons
function PlusIcon() {
  return (
    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  );
}
